function userKey(user) {
    return typeof user === "string" ? user : user?.key;
}

function shouldUserBeInHub(folder, selectedHubKeys) {
    return (
        selectedHubKeys.has(folder.key) ||
        (folder.ancestors || []).some((key) => selectedHubKeys.has(key))
    );
}

export function cloneGroupsForHub(groups, hubKey) {
    return groups
        .filter((group) => group.hub === hubKey)
        .map((group) => ({
            ...group,
            users: (group.users || []).map((user) => ({
                key: userKey(user),
            })),
        }));
}

export function createHubDraft(folder, groups, existingDraft) {
    return {
        ...folder,
        managers: [...(folder.managers || [])],
        users: [...(folder.users || [])],
        ancestors: [...(folder.ancestors || [])],
        groups: existingDraft
            ? existingDraft.groups.map((group) => ({
                  ...group,
                  users: (group.users || []).map((user) => ({
                      key: userKey(user),
                  })),
              }))
            : cloneGroupsForHub(groups, folder.key),
    };
}

export function buildMembershipUpdates({
    folders,
    groups,
    selectedHubs,
    savedUserKey,
    draftUserKey,
}) {
    const updates = {};
    const selectedHubKeys = new Set(selectedHubs.map((hub) => hub.key));
    const draftKeys = new Set(
        [savedUserKey, draftUserKey, "newuser"].filter(Boolean),
    );

    folders
        .filter((folder) => folder.hub)
        .forEach((folder) => {
            const shouldBeMember = shouldUserBeInHub(
                folder,
                selectedHubKeys,
            );
            const isCurrentlyMember = (folder.users || []).includes(
                savedUserKey,
            );

            if (shouldBeMember !== isCurrentlyMember) {
                updates[
                    `folders/${folder.key}/users/${savedUserKey}`
                ] = shouldBeMember ? true : null;
            }

            const allUsersGroup = groups.find(
                (group) =>
                    group.hub === folder.key && group.name === "All Users",
            );

            if (allUsersGroup) {
                const isCurrentlyInAllUsers = (
                    allUsersGroup.users || []
                ).some((user) => userKey(user) === savedUserKey);

                if (shouldBeMember !== isCurrentlyInAllUsers) {
                    updates[
                        `groups/${allUsersGroup.key}/users/${savedUserKey}`
                    ] = shouldBeMember ? true : null;
                }
            }
        });

    groups
        .filter((group) => group.name !== "All Users")
        .forEach((group) => {
            const draftGroup = selectedHubs
                .find((hub) => hub.key === group.hub)
                ?.groups.find((candidate) => candidate.key === group.key);
            const userIsSelected = draftGroup?.users.some((user) =>
                draftKeys.has(userKey(user)),
            );
            const isCurrentlySelected = (group.users || []).some(
                (user) => userKey(user) === savedUserKey,
            );

            if (Boolean(userIsSelected) !== isCurrentlySelected) {
                updates[
                    `groups/${group.key}/users/${savedUserKey}`
                ] = userIsSelected ? true : null;
            }
        });

    return updates;
}

/**
 * The managers of every hub this draft leaves the user a member of.
 *
 * Membership is inherited by descendant hubs (see shouldUserBeInHub) and a
 * descendant can carry managers its parent does not, so this has to walk the
 * hubs the user ends up in rather than the ones ticked in the dialog.
 * backfillUserAdmins.hubDerivedAdmins is the same rule applied in bulk.
 */
function derivedAdminKeys(folders, selectedHubKeys) {
    const adminKeys = new Set();

    folders
        .filter(
            (folder) =>
                folder.hub && shouldUserBeInHub(folder, selectedHubKeys),
        )
        .forEach((folder) =>
            (folder.managers || []).forEach((managerKey) =>
                adminKeys.add(managerKey),
            ),
        );

    return adminKeys;
}

/**
 * The admins the user should be left with once this draft is saved.
 *
 * Additive apart from the hubs this save takes the user out of. A manager who
 * administers them for some other reason keeps that: a grant this form did not
 * make is not this form's to revoke, and saving a phone number should not
 * quietly change who can see the person.
 */
export function resolveUserAdmins({
    folders,
    selectedHubs,
    savedUserKey,
    savedAdmins,
}) {
    const selectedHubKeys = new Set(selectedHubs.map((hub) => hub.key));
    const derived = derivedAdminKeys(folders, selectedHubKeys);
    const revoked = new Set();

    folders
        .filter(
            (folder) =>
                folder.hub &&
                (folder.users || []).includes(savedUserKey) &&
                !shouldUserBeInHub(folder, selectedHubKeys),
        )
        .forEach((folder) =>
            (folder.managers || []).forEach((managerKey) => {
                // Still a manager of somewhere the user remains a member of,
                // so leaving this hub does not cost them the grant.
                if (!derived.has(managerKey)) {
                    revoked.add(managerKey);
                }
            }),
        );

    const kept = (savedAdmins || []).filter(
        (adminKey) => !revoked.has(adminKey),
    );

    return [...new Set([...kept, ...derived])].sort();
}

// The same result as resolveUserAdmins, written as the paths that get there.
// Per-key so a concurrent grant elsewhere is not clobbered by a whole-node set.
export function buildAdminUpdates({
    folders,
    selectedHubs,
    savedUserKey,
    savedAdmins,
}) {
    const updates = {};
    const existing = new Set(savedAdmins || []);
    const next = new Set(
        resolveUserAdmins({
            folders,
            selectedHubs,
            savedUserKey,
            savedAdmins,
        }),
    );

    next.forEach((adminKey) => {
        if (!existing.has(adminKey)) {
            updates[`users/${savedUserKey}/admins/${adminKey}`] = true;
        }
    });

    existing.forEach((adminKey) => {
        if (!next.has(adminKey)) {
            updates[`users/${savedUserKey}/admins/${adminKey}`] = null;
        }
    });

    return updates;
}
